import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';
import { Button, Pill } from '@storable/canaveral-components';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { getPageStatusProps } from 'utils/pageStatus';
import Icon from '../../../../../../modules/react/components/stateless/Icon';
import styles from './Language.scss';

const momentFormat = 'MM/DD/YYYY [at] hh:mm a';
const propTypes = {
  onEnable: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      enabled: PropTypes.bool,
      code: PropTypes.string.isRequired,
    }),
  ),
  siteLanguages: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string.isRequired,
    enabled: PropTypes.boolean,
  })),
  children: PropTypes.arrayOf(PropTypes.object),
  pushRoute: PropTypes.func.isRequired,
  creatingChild: PropTypes.bool.isRequired,
};

const defaultProps = {
  languages: [],
};

const getPopoverProps = (page, child) => {
  if (child) {
    return {
      ...child,
      route: `/admin/page/edit/${child.pageId}`,
    };
  }
  return {
    ...page,
    route: `/admin/page/edit/${page.id}`,
    status: getPageStatusProps({ ...page, key: 'id' }),
  };
};

const Language = ({
  siteLanguages,
  languages,
  onEnable,
  children,
  pushRoute,
  creatingChild,
  ...page
}) => (
  <div className={styles.languages}>
    {siteLanguages.map(({ code }) => {
      const diameter = code.length <= 2 ? 25 : null;
      const child = children.find(kid => kid.locale === code);
      const {
        lastUpdated, lastPublished, status, route,
      } = getPopoverProps(page, child);
      const pageLanguage = languages.find(language => language.code === code);
      const pageLanguageEnabled = pageLanguage.enabled;
      const { name } = pageLanguage;

      const classes = cx(
        styles.pill,
        pageLanguageEnabled && styles.enabled,
      );

      const buttonClasses = cx(
        styles.enableButton,
        diameter && styles.circular,
      );

      const popover = pageLanguageEnabled ? (
        <Popover
          className="languageTooltip"
          id={`enabled-language-popover-${code}`}
          key={`language-popover-${code}`}
        >
          <div className={styles.languageName}>{name}</div>
          <div className={styles.size15SVG}>
            <Icon
              label="page status"
              {...status}
            />{status.text}
          </div>
          <div className={styles.timestamps}>
            {lastUpdated && <div>updated {moment(lastUpdated).local().format(momentFormat)}</div>}
            {lastPublished &&
              <div className={styles.grey}>published {moment(lastPublished).local().format(momentFormat)}</div>}
          </div>
        </Popover>
      ) : (
        <Popover
          className={styles.popover}
          id={`add-language-popover${code}`}
          key={code}
        >
          <div className={styles.languageName}>{name}</div>
          <div>This language hasn't been enabled for this page yet.</div>
        </Popover>
      );

      const pill = (
        <Pill
          inline
          key={code}
          className={classes}
          diameter={diameter}
        >
          {code.toUpperCase()}
        </Pill>
      );

      return (
        <OverlayTrigger
          key={code}
          overlay={popover}
          placement="bottom"
        >
          {pageLanguageEnabled ?
            <div className={styles.pillWrapper} onClick={() => pushRoute(route)}>{pill}</div>
            :
            <div className={styles.pillWrapper}>
              <Button
                icon="add-group"
                context="positive"
                label={`Enable ${name}`}
                onClick={() => onEnable(page, code)}
                className={buttonClasses}
                disabled={creatingChild}
              />
              {pill}
            </div>
          }
        </OverlayTrigger>
      );
    })}
  </div>
);

Language.propTypes = propTypes;
Language.defaultProps = defaultProps;

export default Language;
